import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import * as FB from '../firebase';

export const fetchPostsFirstBatch = createAsyncThunk('fetchPostsFirstBatch', async (params = {}) => await FB.fetchPostsFirstBatch(params.userId))
export const fetchPostsNextBatch = createAsyncThunk('fetchPostsNextBatch', async ({ lastCreatedAt, userId }) => await FB.fetchPostsNextBatch(lastCreatedAt, userId))
export const editPost = createAsyncThunk('editPost', async ({ postId, text }) => await FB.updatePost(postId, text))
export const deletePost = createAsyncThunk('deletePost', async ({ postId }) => await FB.deletePost(postId))

export const createComment = createAsyncThunk('createComment', async ({ postId, userId, text, postAuthorId }) => await FB.createComment(postId, userId, text, postAuthorId))
export const editComment = createAsyncThunk('editComment', async ({ commentId, postId, text }) => await FB.updateComment(commentId, postId, text))
export const deleteComment = createAsyncThunk('deleteComment', async ({ commentId, postId }) => await FB.deleteComment(commentId, postId))

export const createReaction = createAsyncThunk('createReaction', async ({ postId, userId, postAuthorId }) => await FB.createReaction(postId, userId, postAuthorId))
export const deleteReaction = createAsyncThunk('deleteReaction', async ({ reactionId, postId }) => await FB.deleteReaction(reactionId, postId))

const postsSlice = createSlice({
  name: 'posts',
  initialState: {
    posts: [],
    isLoading: false,
    processingReaction: false,
    isLoadingMorePosts: false,
    areThereMorePosts: true,
  },
  extraReducers: builder => {
    // Fetch Posts
    builder.addCase(fetchPostsFirstBatch.pending, state => {
      state.posts = []
      state.isLoading = true
      state.areThereMorePosts = true
    })
    builder.addCase(fetchPostsFirstBatch.fulfilled, (state, action) => {
      if (action.payload) {
        state.posts = action.payload
      }
      state.isLoading = false
    })
    builder.addCase(fetchPostsFirstBatch.rejected, (state, action) => {
      state.isLoading = false
      console.error('fetchPostsFirstBatch.rejected', action.error)
    })

    // Fetch More Posts
    builder.addCase(fetchPostsNextBatch.pending, state => {
      state.isLoadingMorePosts = true
    })
    builder.addCase(fetchPostsNextBatch.fulfilled, (state, action) => {
      if (action.payload) {
        state.posts = [...state.posts, ...action.payload]

        if (action.payload.length === 0) {
          state.areThereMorePosts = false
        }
      }
      state.isLoadingMorePosts = false
    })
    builder.addCase(fetchPostsNextBatch.rejected, state => {
      state.isLoadingMorePosts = false
      console.error('fetchPostsNextBatch.rejected')
    })

    // Edit Post
    builder.addCase(editPost.fulfilled, (state, action) => {
      try {
        const { postId, text } = action.payload

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post) {
          post.text = text
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }
    })
    builder.addCase(editPost.rejected, state => {
      console.error('editPost.rejected')
    })

    // Delete Post
    builder.addCase(deletePost.fulfilled, (state, action) => {
      try {
        const { postId } = action.payload
        const postIdx = state.posts.findIndex(post => post.id === postId)

        state.posts = [...state.posts.slice(0, postIdx), ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }
    })
    builder.addCase(deletePost.rejected, state => {
      console.error('deletePost.rejected')
    })

    // Create Comments
    builder.addCase(createComment.fulfilled, (state, action) => {
      try {
        const comment = action.payload
        const postId = action.payload.postId

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post.comments) {
          post.comments[comment.commentId] = comment
        } else {
          post.comments = {
            [comment.commentId]: comment
          }
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }
    })
    builder.addCase(createComment.rejected, state => {
      console.error('createComment.rejected')
    })

    // Edit Comments
    builder.addCase(editComment.fulfilled, (state, action) => {
      try {
        const { commentId, postId, text } = action.payload

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post.comments) {
          post.comments[commentId].text = text
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }
    })
    builder.addCase(editComment.rejected, state => {
      console.error('editComment.rejected')
    })

    // Delete Comments
    builder.addCase(deleteComment.fulfilled, (state, action) => {
      try {
        const { commentId, postId } = action.payload

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post.comments) {
          delete post.comments[commentId]
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }
    })
    builder.addCase(deleteComment.rejected, (state, action) => {
      console.error('deleteComment.rejected', action.error)
    })

    // Create Reactions
    builder.addCase(createReaction.pending, (state) => {
      state.processingReaction = true
    })
    builder.addCase(createReaction.fulfilled, (state, action) => {
      try {
        const reaction = action.payload
        const postId = action.payload.postId

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post.reactions) {
          post.reactions[reaction.reactionId] = reaction
        } else {
          post.reactions = {
            [reaction.reactionId]: reaction
          }
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }

      state.processingReaction = false
    })
    builder.addCase(createReaction.rejected, (state, action) => {
      state.processingReaction = false
      console.error('createReaction.rejected', action.error)
    })

    // Delete Reactions
    builder.addCase(deleteReaction.pending, state => {
      state.processingReaction = true
    })
    builder.addCase(deleteReaction.fulfilled, (state, action) => {
      try {
        const { reactionId, postId } = action.payload

        const postIdx = state.posts.findIndex(post => post.id === postId)
        const post = state.posts[postIdx]

        if (post.reactions) {
          delete post.reactions[reactionId]
        }

        state.posts = [...state.posts.slice(0, postIdx), post, ...state.posts.slice(postIdx + 1)]
      } catch (err) {
        console.error(err)
      }

      state.processingReaction = false
    })
    builder.addCase(deleteReaction.rejected, state => {
      state.processingReaction = false
      console.error('deleteReaction.rejected')
    })
  }
})

export default postsSlice
